import { useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { OperatorDeviceCommandType } from '@ghostscreen/contracts';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { apiErrorMessage } from '../i18n';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { useOrganisationId } from '../lib/session';
import {
  buildFixesHero,
  buildRemoteFixRow,
  buildTabCounts,
  emptyFixesCopy,
  filterRecordsForTab,
  type FixesPeriod,
  type FixesTab,
  type RemoteFixRow,
} from './remoteFixes/buildRemoteFixesModel';
import { RemoteFixesHero } from './remoteFixes/RemoteFixesHero';
import { RemoteFixesList } from './remoteFixes/RemoteFixesList';
import { RemoteFixesToolbar } from './remoteFixes/RemoteFixesToolbar';

const REFRESH_INTERVAL_MS = 15_000;
const PAGE_SIZE = 25;

/**
 * Remote Fixes page — human log of restarts/refreshes
 * (Ghost Screen Remote Fixes.dc.html). Route stays /command-operations.
 */
export function CommandOperationsPage() {
  const { t } = useTranslation(['operations', 'common']);
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<FixesTab>('All');
  const [period, setPeriod] = useState<FixesPeriod>('7d');
  const [screenId, setScreenId] = useState('');
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });

  const screens = screensQuery.data ?? [];
  const locations = locationsQuery.data ?? [];
  const selectedScreen = screens.find((screen) => screen.id === screenId) ?? null;
  const deviceFilter = selectedScreen?.deviceId ?? null;

  const filterKey = [organisationId, period, deviceFilter] as const;
  const filterQuery = {
    range: period,
    ...(deviceFilter ? { deviceId: deviceFilter } : {}),
  };

  const summaryQuery = useQuery({
    queryKey: ['command-operations-summary', ...filterKey],
    queryFn: () => api.getCommandOperationsSummary(organisationId, filterQuery),
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const recordsQuery = useInfiniteQuery({
    queryKey: ['command-operations-records', ...filterKey],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.listCommandOperationsRecords(organisationId, {
        ...filterQuery,
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const { isRetrying, retry } = useQueryRetry(async () => {
    await Promise.all([summaryQuery.refetch(), recordsQuery.refetch()]);
  });

  const items = recordsQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const filtered = filterRecordsForTab(items, tab);
  const rows = useMemo(
    () => filtered.map((item) => buildRemoteFixRow(item, screens, locations)),
    [filtered, screens, locations],
  );

  const hero = buildFixesHero(summaryQuery.data);
  const tabCounts = buildTabCounts(summaryQuery.data);

  const retryFix = useMutation({
    mutationFn: (row: RemoteFixRow) =>
      api.sendDeviceCommand(
        organisationId,
        row.deviceId,
        { type: row.type as OperatorDeviceCommandType },
        crypto.randomUUID(),
      ),
    onMutate: (row) => {
      setRetryingId(row.id);
      setRetryError(null);
    },
    onSuccess: async () => {
      setRetryingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['command-operations-summary', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['command-operations-records', organisationId] }),
      ]);
    },
    onError: (err) => {
      setRetryingId(null);
      setRetryError(apiErrorMessage(err, t('retryError')));
    },
  });

  const loading =
    summaryQuery.isPending ||
    recordsQuery.isPending ||
    screensQuery.isPending ||
    locationsQuery.isPending;
  const failed =
    summaryQuery.isError ||
    recordsQuery.isError ||
    screensQuery.isError ||
    locationsQuery.isError;

  return (
    <div className="flex flex-col gap-[22px]" style={{ animation: 'gs-in 260ms ease both' }}>
      <RemoteFixesHero title={hero.title} subtitle={hero.subtitle} color={hero.color} />

      <RemoteFixesToolbar
        tab={tab}
        tabCounts={tabCounts}
        onTabChange={setTab}
        period={period}
        onPeriodChange={setPeriod}
        screenId={screenId}
        onScreenChange={setScreenId}
        screens={screens}
      />

      {retryError ? (
        <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
          {retryError}
        </p>
      ) : null}

      {failed || isRetrying ? (
        <QueryErrorState
          message={t('loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock variant="rows" />
      ) : rows.length === 0 ? (
        <div
          className="flex flex-col items-center gap-1.5 rounded-[14px] border-[1.5px] border-dashed px-6 py-9 text-center"
          style={{ borderColor: 'var(--gs-line)' }}
        >
          <span className="text-base font-bold" style={{ color: 'var(--gs-ink)' }}>
            {t('empty.title')}
          </span>
          <span
            className="max-w-[420px] text-sm"
            style={{ color: 'var(--gs-muted)', textWrap: 'pretty' }}
          >
            {emptyFixesCopy(tab)}
          </span>
        </div>
      ) : (
        <>
          <RemoteFixesList
            rows={rows}
            onRetry={(row) => retryFix.mutate(row)}
            retryingId={retryingId}
          />
          {recordsQuery.hasNextPage ? (
            <button
              type="button"
              onClick={() => void recordsQuery.fetchNextPage()}
              disabled={recordsQuery.isFetchingNextPage}
              className="self-start rounded-[9px] border px-3.5 py-2 text-sm font-bold disabled:opacity-50"
              style={{
                color: 'var(--gs-ink)',
                background: 'var(--gs-panel)',
                borderColor: 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
            >
              {recordsQuery.isFetchingNextPage ? t('common:states.loading') : t('loadMore')}
            </button>
          ) : null}
        </>
      )}

      <p
        className="m-0 max-w-[680px] text-[13px]"
        style={{ color: 'var(--gs-muted)', lineHeight: 1.5, textWrap: 'pretty' }}
      >
        {t('howTo', { hours: 24 })}
      </p>
    </div>
  );
}
